'use server';

import { mailLocale } from '@/lib/mail-locale';
import { itemInviteMail, renderMail } from '@/domain/mail';
import { randomUUID } from 'node:crypto';
import { revalidatePath } from 'next/cache';
import {
  LEVELS,
  type DecisionOutcome,
  type Level,
  type MeetingRole,
  type ModuleKey,
  type Role,
} from '@/domain/constants';
import { extractHashtags, foldTag, type TagOption } from '@/domain/item-tags';
import { formatRef, parseRef } from '@/domain/refs';
import type { MachineScope } from '@/domain/machine-scope';
import {
  type AcceptFollowUp,
  type Command,
  domainError,
  errOf,
  type DomainError,
} from '@/domain/types';
import type { StatementInstanceRef } from '@/domain/qualified-facts';
import { m, type Message } from '@/i18n';
import {
  boardMemberItemIds,
  flowsOfItems,
  commonsOrgId,
  listContactsFor,
  listMembershipAsksFor,
  listWithdrawnAsksFor,
  type ContactLists,
  type MembershipAskView,
  type WithdrawnAskView,
  getGroupAccessDetail,
  getItemOrg,
  getOpenableItemOrg,
  mentionSeqsOn,
  standingAskSeqs,
  getViewAccess,
  getMyElevation,
  getPushRule,
  getGroup,
  itemDecisionOptions,
  listCeremonies,
  listFlows,
  listPlans,
  readListRows,
  scheduleConflictCosts,
  searchWorkItems,
  searchWorkItemsFiltered,
  retagReach,
  stageMoveOptions,
  tagCandidates,
  type ActiveElevation,
  type GroupAccessDetail,
  type ItemDecisionOptions,
  type ItemSearchPage,
  type ListRow,
  type StageMoveOptionView,
  type ViewAccessDetail,
  type WorkItemHit,
} from '@/read';
import { normalizeCriteria } from '@/domain/search-criteria';
import { directActor, type ActorContext } from '@/server/actor';
import { act, finishChain, reviewWithFollowUps } from '@/server/resolution';
import { retag } from '@/server/retag';
import { removeAttachment as removeAttachmentOnSpine } from '@/server/attachments';
import {
  decodePendingStimulus,
  stimulusOf,
  type Answer,
  type PendingStimulus,
  type Question,
  type Waiting,
} from '@/domain/resolution';
import { createGroup, setGroupDescription } from '@/server/orgs';
import { DomainAbort } from '@/server/event-log';
import {
  acceptMembership,
  acceptNesting,
  addMember,
  declineMembership,
  grantRole,
  proposeNesting,
  removeMember,
  requestAccess,
  requestInheritedAccess,
  revokeRole,
  setMemberRole,
} from '@/server/access';
import {
  setModuleField as serverSetModuleField,
  setItemModulePresence as serverSetItemModulePresence,
} from '@/server/modules';
import {
  archiveView,
  bulkGrantItems,
  configureBoard,
  createBoard,
  grantViewAccess,
  mintSharedView,
  pinItem,
  renameView,
  restoreView,
  revokeViewAccess,
  setItemLane,
  setBoardItemHidden as serverSetBoardItemHidden,
  setBoardItemOrder as serverSetBoardItemOrder,
  setChecklistOrder,
  unpinItem,
  type BulkGrantItemsResult,
  type ConfigureBoardInput,
  type CreateBoardInput,
  type MintSharedViewInput,
  type PinFlowJoin,
} from '@/server/views';
import { createLens } from '@/server/lenses';
import { actOnLine, demoteChild, promoteLines } from '@/server/checklist';
import type { Checklist, LineAct } from '@/domain/checklist';
import {
  addWorkToGate,
  advanceFlowMember,
  holdFlowMember,
  joinFlow,
  leaveFlow,
  moveFlowMember,
  waiveGateCondition,
} from '@/server/flows';
import { runFlowAutomation } from '@/server/flow-automation';
import { deescalate, elevate } from '@/server/elevation';
import {
  addCeremonyItem,
  closeCeremony,
  conveneCeremony,
  declineCeremonyItem,
  executeCeremonyItem,
  setCeremonyParticipant,
} from '@/server/ceremonies';
import type { CeremonyParticipant } from '@/domain/ceremonies';
import { createWorkItem, declineSeat, execute, offerSeat, takeUpSeat } from '@/server/work-items';
import {
  replayCommands as replayQueuedCommands,
  type QueuedCommand,
  type ReplayOutcome,
} from '@/server/replay';
import { setPushRule, type PushRuleSetting } from '@/server/push-rules';
import { clearAllHealth, clearQualification, qualifyFact, setTagByKey } from '@/server/tags';
import { claimInvite, createInvite, revokeInvite } from '@/server/work-item-invites';
import {
  acceptContact,
  blockContact,
  removeContact,
  requestContact as serverRequestContact,
  unblockContact,
  withdrawContactRequest,
} from '@/server/contacts';
import { clearHandle, setHandle } from '@/server/handles';
import { mintMachineKey, revokeMachineKey } from '@/server/machine-keys';
import { citedStatement } from '@/read/statement-handles';
import { sendMail } from '@/lib/mail';
import {
  addChange,
  applyChange,
  createPlan,
  createProposal,
  declineChange,
  executePlan,
  proposeAct,
  proposeItemChange,
  publishProposal,
  removeChange,
  stageChangeInPlan,
} from '@/server/proposals';
import {
  acceptRelationship,
  convertRelationship,
  createAndLink,
  promoteDependency,
  proposeRelationship,
  removeRelationship,
  setChildOrder,
  setRelationshipKind,
  setRelationshipShare,
} from '@/server/relationships';
import type { RelationshipKind } from '@/domain/constants';
import { headers } from 'next/headers';
import { auth } from '@/lib/auth';
import { env } from '@/env';
import { DEMO_PASSWORD, demoEmail, devSignInTarget } from '@/lib/directory';
import { requireViewer, withResolvedRequest } from '@/lib/viewer';
import { starView, unstarView } from '@/lib/view-stars';
import { recordItemView } from '@/lib/item-views';
import { recordNotificationActs } from '@/lib/notification-state';
import { recordViewOpen, recordViewOpenAtMint } from '@/lib/view-opens';

/**
 * The shared result of a command action. On success, `at` (epoch millis) is the
 * resulting `updatedAt` of the last write, when the action ran the work-item
 * spine (docs/SYNC.md §7.3/§11: an action returns its write's marker so the
 * optimistic store can reconcile). Optional because most actions on this shape
 * write elsewhere (access, groups, views) and carry no such marker; `runCommand`
 * populates it.
 */
export type CommandResult =
  | {
      ok: true;
      at?: number;
      /** The gesture was not taken but ASKED (a propose lane that landed):
       *  nothing changed on the item, and the surface says who was asked. */
      asked?: AskedAnswer;
    }
  | { ok: false; error: DomainError };
/**
 * A propose lane that LANDED (docs/RESOLUTION.md §12 step 8): the gesture was
 * filed with the item's owners as a proposal carrying the act. The item did
 * not change. The surface says it in the owners' names ("asked Ari"), which it
 * resolves from its own directory - the server hands refs, never names.
 */
export interface AskedAnswer {
  /** The sentence the owner reads, from a dry run as them, as a descriptor the
   *  surface formats in its viewer's locale. */
  statement: Message;
  /** Who may answer: the item's requesters when it was raised. */
  answerers: string[];
  changeId: string;
}
/** What a tick answers with (docs/RESOLUTION.md §2, §10): the plan's one
 *  sentence, what the item still waits on, and the new truth of EVERY item the
 *  plan touched - the tapped line and the sub-tasks its sweep took with it
 *  (§6) - for the surface to patch. An item the viewer may no longer see is
 *  simply absent (the gate answers by omission). */
export type TickResult =
  | { ok: true; statement: Message; waits?: Waiting; rows: ListRow[]; at: number }
  | { ok: true; asked: AskedAnswer }
  | { ok: false; error: DomainError };
export type LinkResult =
  | { ok: true; status: 'applied' | 'requested' }
  | { ok: false; error: DomainError };
export type CreateResult = { ok: true; id: string } | { ok: false; error: DomainError };
/** A create-and-link's reply: the new item's id (the unsaved row becomes it)
 *  and how the edge landed. */
export type CreateLinkResult =
  | { ok: true; id: string; status: 'applied' | 'requested' }
  | { ok: false; error: DomainError };

/** The acting principal: the current viewer, read server-side so the client cannot spoof it. */
async function viewerActor(): Promise<ActorContext> {
  return directActor(parseRef(await requireViewer()));
}

/**
 * Resolve the org for an item, call fn with (orgId, actor), and revalidate on
 * success. Eliminates the `getItemOrg → gate → call → revalidate` boilerplate
 * for item-scoped CommandResult actions (relationship + access writes).
 * Called only inside a withResolvedRequest boundary (the exported action wraps it).
 */
async function runItemCommand(
  itemId: string,
  fn: (orgId: string, actor: ActorContext) => Promise<CommandResult>,
): Promise<CommandResult> {
  return runInOrg(await getItemOrg(itemId), fn);
}

/**
 * `runItemCommand` for the relationship acts: the item must be one the viewer
 * can open (`getOpenableItemOrg`), else the act answers as for an id that names
 * no item. The edge the act names is then checked by the spine against the
 * actor's sight of its two ends (`loadEdgeFor`, src/server/relationships.ts).
 */
async function runEdgeCommand(
  itemId: string,
  fn: (orgId: string, actor: ActorContext) => Promise<CommandResult>,
): Promise<CommandResult> {
  return runInOrg(await openableOrgOf(itemId), fn);
}

/** The org of an item the current viewer can open, else null. */
async function openableOrgOf(itemId: string): Promise<string | null> {
  return getOpenableItemOrg(itemId, await requireViewer());
}

async function runInOrg(
  orgId: string | null,
  fn: (orgId: string, actor: ActorContext) => Promise<CommandResult>,
): Promise<CommandResult> {
  if (!orgId) return { ok: false, error: domainError('not_found', m('common.error.itemNotFound')) };
  const res = await fn(orgId, await viewerActor());
  if (res.ok) revalidatePath('/', 'layout');
  return res;
}

/**
 * Lazy load for the "Manage access" modal: the group's access detail (members expanded
 * with provenance, nesting both directions), resolved for the current viewer. A read, but
 * exposed as an action so the client modal can fetch it on open rather than the board
 * computing the expansion on every render. Returns null if the group is gone.
 */
export async function loadGroupAccess(groupId: string): Promise<GroupAccessDetail | null> {
  return withResolvedRequest(async () => getGroupAccessDetail(groupId, await requireViewer()));
}

/**
 * Content search for the quick switcher (docs/SEARCH.md Tier 2) and the board
 * Add bar (docs/VIEWS.md §12 T1): work items by their text, ranked and
 * access-gated server-side for the real viewer - the client only ever sees rows
 * it may see. A read exposed as an action so the surface can query as-you-type.
 *
 * `viewId` makes the search **board-aware**: each hit carries a `canPin` computed
 * from the EXACT same §2b share-authority gate `pinItem` runs, so the Add bar can
 * mute (show, but refuse) a hit you can see yet may not pin here. Omit it for the
 * global switcher, which navigates rather than pins (canPin then defaults true).
 */
export async function searchItems(
  query: string,
  viewId?: string,
  showClosed?: boolean,
  canRoot?: boolean,
): Promise<WorkItemHit[]> {
  return withResolvedRequest(async () =>
    searchWorkItems(query, await requireViewer(), {
      limit: 8,
      viewId,
      showClosed,
      // The root picker asks for each hit's member reach; every other caller
      // leaves it unread (`WorkItemHit.canRoot`).
      canRoot: canRoot === true,
    }),
  );
}

/** An item a prose reference may name (docs/PROSE.md §5.1): its id, its title
 *  and its glyph, nothing else. */
export interface ReferenceItem {
  id: string;
  title: string;
  glyph: WorkItemHit['glyph'];
}

export type ReferenceItemsResult =
  | { ok: true; items: ReferenceItem[] }
  | { ok: false; error: DomainError };

/**
 * The prose editor's `#` list (docs/PROSE.md §5.1): the items whose text
 * matches `query`, through the sighted search read (`searchWorkItems`), so the
 * list offers only items the viewer can open. Takes the query and nothing
 * else; returns eight hits at most, each its id, title and glyph. A read, so
 * nothing is written; a query shorter than two characters finds nothing.
 */
export async function searchReferenceItemsAction(query: string): Promise<ReferenceItemsResult> {
  return withResolvedRequest(async () => {
    const viewer = await requireViewer();
    if (typeof query !== 'string') return { ok: true, items: [] };
    const hits = await searchWorkItems(query, viewer, { limit: 8 });
    return {
      ok: true,
      items: hits.map(({ id, title, glyph }) => ({ id, title, glyph })),
    };
  });
}

/**
 * The quick-open palette's lazy target catalog: flows, plans, and ceremonies
 * the viewer can see (each read is viewer-gated), fetched once per palette
 * open instead of riding the layout on every request. Names + ids only.
 */
export async function quickOpenTargets(): Promise<{
  flows: { id: string; name: string }[];
  plans: { id: string; name: string }[];
  ceremonies: { id: string; title: string }[];
}> {
  return withResolvedRequest(async () => {
    const viewer = await requireViewer();
    const [flows, plans, cers] = await Promise.all([
      listFlows(viewer),
      listPlans(viewer),
      listCeremonies(viewer),
    ]);
    return {
      flows: flows.filter((f) => f.status === 'active').map((f) => ({ id: f.id, name: f.name })),
      plans: plans.map((p) => ({ id: p.id, name: p.name })),
      ceremonies: cers.map((c) => ({ id: c.id, title: c.title })),
    };
  });
}

/**
 * The create-view modal's lazy flow options (docs/VIEWS.md §6; the ledgered
 * create-modal gap): the viewer's REACHABLE, ACTIVE flows - `listFlows`, the
 * same per-linker universe board-config's link options draw from - fetched
 * once when the modal opens instead of riding the layout on every request.
 * Names + ids only; the create itself re-gates server-side
 * (`assertLinkableFlow` in `createBoard`).
 */
export async function flowLinkOptions(): Promise<{ id: string; name: string }[]> {
  return withResolvedRequest(async () => {
    const flows = await listFlows(await requireViewer());
    return flows.filter((f) => f.status === 'active').map((f) => ({ id: f.id, name: f.name }));
  });
}

/**
 * Criteria-capable content search (docs/SEARCH.md phase 3): free text plus the
 * shared criterion grammar, for the palette's filter chips and /search's
 * paging. The wire input is untrusted: criteria run through `normalizeCriteria`
 * server-side, op is coerced, and the window is clamped - a hand-crafted call
 * can never widen what the gated read returns.
 */
export async function searchItemsFiltered(input: {
  q: string;
  criteria: unknown;
  op?: string;
  limit?: number;
  offset?: number;
  showClosed?: boolean;
}): Promise<ItemSearchPage> {
  return withResolvedRequest(async () =>
    searchWorkItemsFiltered(
      {
        q: typeof input.q === 'string' ? input.q : '',
        criteria: normalizeCriteria(input.criteria),
        op: input.op === 'any' ? 'any' : 'all',
      },
      await requireViewer(),
      {
        limit: Math.min(Math.max(1, Math.trunc(input.limit ?? 25)), 50),
        offset: Math.max(0, Math.trunc(input.offset ?? 0)),
        showClosed: input.showClosed === true,
      },
    ),
  );
}

/**
 * Relationship edges (docs/RELATIONSHIPS.md), all two-sided: the server applies the
 * edge when the actor reaches both ends (or the far end is open), else leaves it
 * pending the protected side's approval. The org is resolved from an endpoint the
 * caller already holds, so the client passes only ids.
 */
export async function linkRelationship(input: {
  fromItemId: string;
  toItemId: string;
  kind: RelationshipKind;
  share?: number | null;
}): Promise<LinkResult> {
  return withResolvedRequest(async () => {
    const orgId = await getItemOrg(input.fromItemId);
    if (!orgId)
      return { ok: false, error: domainError('not_found', m('common.error.itemNotFound')) };
    const res = await proposeRelationship({ orgId, ...input }, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, status: res.value.status };
  });
}

/**
 * Create a brand-new item and link it to an existing one in a single move
 * (`createAndLink`, src/server/relationships.ts): the existing item is checked
 * before anything is created, and one the viewer cannot open answers exactly
 * as a missing id.
 */
export async function createAndLinkRelationship(input: {
  title: string;
  otherItemId: string;
  kind: RelationshipKind;
  newSide: 'from' | 'to';
  /** The create's idempotency key, minted at the gesture; a retry resends it
   *  (docs/LOADING.md §3.4). */
  key: string;
}): Promise<CreateLinkResult> {
  return withResolvedRequest(async () => {
    const typed = titleAndTags(input.title);
    if (!typed.ok) return typed;
    const { title, tags } = typed;
    const res = await createAndLink(
      { title, tags, otherItemId: input.otherItemId, kind: input.kind, newSide: input.newSide },
      await viewerActor(),
      { commandKey: input.key },
    );
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, id: res.value.id, status: res.value.status };
  });
}

export async function acceptRelationshipLink(input: {
  relId: string;
  itemId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(() =>
    runEdgeCommand(input.itemId, (orgId, actor) =>
      acceptRelationship({ orgId, relId: input.relId }, actor),
    ),
  );
}

export async function removeRelationshipLink(input: {
  relId: string;
  itemId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(() =>
    runEdgeCommand(input.itemId, (orgId, actor) =>
      removeRelationship({ orgId, relId: input.relId }, actor),
    ),
  );
}

/** Retype a lateral edge in place - the standard type control: hard `blocks`
 *  ("blocker" / "feeds", `swap` flips who waits on whom) <-> soft `relates`
 *  ("related, optional"). Containment stays a remove + add (approval lane). */
export async function setRelationshipKindAction(input: {
  relId: string;
  itemId: string;
  kind: 'blocks' | 'relates';
  swap?: boolean;
}): Promise<CommandResult> {
  return withResolvedRequest(() =>
    runEdgeCommand(input.itemId, (orgId, actor) =>
      setRelationshipKind({ orgId, relId: input.relId, kind: input.kind, swap: input.swap }, actor),
    ),
  );
}

/** Convert an edge to a new SHAPE (kind + direction over the same two items) -
 *  the edit-relationship dialog's commit. Laterals retype in place; anything
 *  touching containment is the sanctioned remove + add in one transaction,
 *  landing `requested` when the far side must approve (LinkResult carries
 *  which, so the dialog can say "sent for approval"). */
export async function convertRelationshipAction(input: {
  relId: string;
  itemId: string;
  kind: 'parent' | 'blocks' | 'relates';
  fromItemId: string;
  toItemId: string;
}): Promise<LinkResult> {
  return withResolvedRequest(async () => {
    const orgId = await openableOrgOf(input.itemId);
    if (!orgId)
      return { ok: false, error: domainError('not_found', m('common.error.itemNotFound')) };
    const res = await convertRelationship(
      {
        orgId,
        relId: input.relId,
        kind: input.kind,
        fromItemId: input.fromItemId,
        toItemId: input.toItemId,
      },
      await viewerActor(),
    );
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, status: res.value.status };
  });
}

export async function setRelationshipShareAction(input: {
  relId: string;
  itemId: string;
  share: number | null;
}): Promise<CommandResult> {
  return withResolvedRequest(() =>
    runEdgeCommand(input.itemId, (orgId, actor) =>
      setRelationshipShare({ orgId, relId: input.relId, share: input.share }, actor),
    ),
  );
}

/**
 * "Make this a promise" (docs/HEALTH_SIGNALS_V2.md §9, HS-K4; seat semantics
 * per HEALTH_SIGNALS_V2_REVIEW.md R5): promote an observed dependency into a
 * boundary contract - one transaction mints the boundary item (requesters =
 * a copy of the dependent's demand side; the viewer is only the initiator),
 * offers the contributor seat (explicit party, or by default the foreign
 * item's requester(s)) per THEIR push rules, re-points the blocks edge, and
 * keeps a provenance edge. `itemId` is the dependent (the edge's near end
 * here), used to resolve the org and revalidate.
 */
export async function promoteDependencyAction(input: {
  relId: string;
  itemId: string;
  title: string;
  /** Omitted: the server defaults to the foreign item's requester(s). */
  contributorRef?: string | null;
  endTime?: string | null;
}): Promise<
  | {
      ok: true;
      boundaryItemId: string;
      offers: { holderRef: string; landing: 'held' | 'offered' }[];
    }
  | { ok: false; error: DomainError }
> {
  return withResolvedRequest(async () => {
    const orgId = await openableOrgOf(input.itemId);
    if (!orgId)
      return { ok: false, error: domainError('not_found', m('common.error.itemNotFound')) };
    const res = await promoteDependency(
      {
        orgId,
        relId: input.relId,
        title: input.title,
        contributorRef: input.contributorRef ?? null,
        endTime: input.endTime ?? null,
      },
      await viewerActor(),
    );
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return {
      ok: true,
      boundaryItemId: res.value.boundaryItemId,
      // `open` is unreachable from an offer (held or offered); narrow for the caller.
      offers: res.value.offers.map((o) => ({
        holderRef: o.holderRef,
        landing: o.landing === 'held' ? ('held' as const) : ('offered' as const),
      })),
    };
  });
}

/**
 * Run a contract command as the current viewer. The state machine enforces what
 * is allowed; this wires the UI to the write spine and revalidates the app. We
 * revalidate the root layout so the list, the board, and the open peek all
 * refresh from one action (correct, and cheap at this scale).
 */
export async function runCommand(input: {
  workItemId: string;
  command: Command;
  groupId?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await execute(input.workItemId, input.command, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok
      ? { ok: true, at: result.value.updatedAt.getTime() }
      : { ok: false, error: result.error };
  });
}

/**
 * Replay a device's offline queue (docs/MOBILE.md §8, docs/SYNC.md §7.4). The
 * commands run in the order the device queued them, each as an ordinary live
 * command decided against current state, each carrying its device time and its
 * idempotency key. A refused one comes back with its typed error and the batch
 * continues; a key already in the log answers from the receipt without
 * appending. Identity is the session, never the payload.
 */
export async function replayCommands(input: {
  commands: QueuedCommand[];
}): Promise<{ results: ReplayOutcome[] } | { ok: false; error: DomainError }> {
  return withResolvedRequest(async () => {
    const res = await replayQueuedCommands(input.commands, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    // One revalidation for the whole batch: the pushes and the device's own
    // answers settle the surfaces, and a queue is one reconnect, not N acts.
    if (res.value.some((r) => r.ok)) revalidatePath('/', 'layout');
    return { results: res.value };
  });
}

/**
 * Finish an item and the ancestor chain the UI showed the user
 * (`getCascadeCloseChain`, src/read/rollup.ts): the item takes the gesture the
 * person chose (its Done or its Accept), each ancestor its own Done edge - one
 * transaction, all or nothing (`finishChain`, src/server/resolution.ts
 * re-verifies each ancestor before finishing it).
 */
export async function closeCascadeAction(input: {
  itemIds: string[];
  gesture: unknown;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const gesture = decodePendingStimulus(input.gesture);
    if (!gesture.ok) return { ok: false, error: gesture.error };
    const result = await finishChain(
      { itemIds: input.itemIds, gesture: gesture.value },
      await viewerActor(),
    );
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

// --- Seats (docs/CONTRACT_SPINE.md §2): offer / take up / decline / push rules --

/**
 * Offer (push) a seat to another party. The offeree's push rules decide whether
 * it lands `held` (settled) or `offered` (pending take-up); the landing comes
 * back so the UI can say which happened. Actor resolved server-side.
 */
export async function offerSeatAction(input: {
  workItemId: string;
  role: 'requester' | 'contributor';
  holderRef: string;
}): Promise<{ ok: true; landing: 'held' | 'offered' } | { ok: false; error: DomainError }> {
  return withResolvedRequest(async () => {
    const result = await offerSeat(
      input.workItemId,
      { role: input.role, holderRef: input.holderRef },
      await viewerActor(),
    );
    if (!result.ok) return { ok: false, error: result.error };
    revalidatePath('/', 'layout');
    // `open` is unreachable from an offer (a seat lands held or offered); narrow for the caller.
    return { ok: true, landing: result.value.landing === 'held' ? 'held' : 'offered' };
  });
}

/** Take up a seat offered to you: the offer clears, you become a held party. */
export async function takeUpSeatAction(input: {
  workItemId: string;
  role: 'requester' | 'contributor';
  /** For group-addressed offers: the group holderRef to seat. When present and a
   *  group ref, the server gates the actor via group membership and seats the group
   *  (not the individual) in the party array (docs/CONTRACT_SPINE.md §3, PR A). */
  holderRef?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await takeUpSeat(
      input.workItemId,
      { role: input.role, holderRef: input.holderRef },
      await viewerActor(),
    );
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * Decline a seat offer. Without `holderRef` this is the offeree declining their
 * own offer; with it, the withdraw affordance (the server enforces the two legal
 * paths: offeree declines, offerer withdraws - anyone else is forbidden).
 */
export async function declineSeatAction(input: {
  workItemId: string;
  role: 'requester' | 'contributor';
  holderRef?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await declineSeat(
      input.workItemId,
      { role: input.role, holderRef: input.holderRef },
      await viewerActor(),
    );
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * Hide the needs-you strip (its ✕, Leon 2026-10-20) until the next ask:
 * records `dismissed` for each ask the strip showed (`seqs`, the events that
 * minted them, `needsYouAsks` in src/app/items/[itemId]/item-reads.ts), so it
 * stays away after a reload and on the viewer's other devices, while a new ask
 * shows again. PRIVATE attention state, off the event log
 * (`src/lib/notification-state.ts`): nothing is appended and nobody else's
 * strip changes.
 *
 * The item id passes one sight check before anything is read or written for
 * it (.claude/rules/write-spine.md rule 7): an item the viewer cannot see
 * answers as an id that names no item, and writes nothing. The check is the
 * read side's (`getOpenableItemOrg`, as for `markItemViewed`), not the write
 * spine's `requireOpenableTx` / `gateForSight`: a pending seat offer gives
 * its offeree sight on the read side only, and that offeree is one of the two
 * people the strip exists for. A seq that keys no ask standing on the item
 * now is dropped, so the store holds only instances that were shown.
 */
export async function dismissNeedsYouAction(input: {
  itemId: string;
  seqs: number[];
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const viewer = await requireViewer();
    if (!(await getOpenableItemOrg(input.itemId, viewer))) {
      return { ok: false, error: domainError('not_found', m('common.error.itemNotFound')) };
    }
    const standing = await standingAskSeqs(input.itemId, viewer);
    const keys = new Set<number>(standing.offers.values());
    if (standing.delivery !== null) keys.add(standing.delivery);
    const asked = Array.isArray(input.seqs) ? input.seqs : [];
    const written = await recordNotificationActs(viewer, 'dismissed', {
      kind: 'needs_you',
      subjectType: 'work_item',
      subjectId: input.itemId,
      eventSeqs: asked.filter((seq) => keys.has(seq)),
    });
    // The page's next render reads the dismissal; revalidating also drops the
    // router's cached copy, so Back to this item does not bring the strip back.
    if (written > 0) revalidatePath('/', 'layout');
    return { ok: true };
  });
}

/**
 * Set a push rule (docs/CONTRACT_SPINE.md §2, D3): who may seat `subjectRef`
 * directly. The spine mutation gates it (self for a person, an owner for a
 * group); `default` returns to the co-member circle by deleting the row.
 */
export async function setPushRuleAction(input: {
  subjectRef: string;
  mode: PushRuleSetting;
  circleRefs?: string[];
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await setPushRule(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * Declare or clear a manual health tag by key (docs/CONTRACT_SPINE.md §5.3),
 * e.g. `blocked`. Declared health is owned by whoever set it; the server
 * resolves the org's catalog row and refuses derived keys (§6). `note` (apply
 * only) is the declarer's own note, riding the event's comment.
 * `grade` (apply only) is the explicit escalation grade (HS-J3: "we got an
 * issue here" re-declares at critical); absent = the catalog grade. The
 * server validates it against the levels.
 */
export async function declareTag(input: {
  workItemId: string;
  key: string;
  on: boolean;
  note?: string;
  grade?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    if (input.grade !== undefined && !(LEVELS as readonly string[]).includes(input.grade)) {
      return errOf('invalid_input', m('item.error.unknownGrade'));
    }
    const result = await setTagByKey(input.workItemId, input.key, input.on, await viewerActor(), {
      note: input.note,
      grade: input.grade as Level | undefined,
    });
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * Choose "On track": clear every plain health declaration and every adoption
 * as one atomic server command. The spine retains one honest clear event per
 * declaration; mirrored signals remain machine-owned.
 */
export async function clearAllHealthAction(input: { workItemId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await clearAllHealth(input.workItemId, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * Declare a qualified statement (docs/HEALTH_SIGNALS_V2.md §3): adopt or
 * contain a cited statement INSTANCE, on the declarer's own item, in the
 * viewer's name. The page cites the statement by its HANDLE
 * (`src/domain/statement-handles.ts`): an anchor never travels from a browser,
 * so the instance is derived here, now, through the reads that showed it, and
 * a handle that matches no current instance is refused. An escalation names
 * the holding declaration instead (`qualificationId`): its stored stance,
 * target, word and citation are re-declared, and only the grade comes from the
 * client. Validation and the contain guard (edge-borne statements only,
 * HS-G6) are the pure domain decide behind the spine's `qualifyFact`.
 */
export async function qualifyFactAction(input: {
  workItemId: string;
  stance: string;
  /** The cited statement's handle. */
  statement?: unknown;
  /** Re-declare this holding declaration (the escalation, HS-J3). */
  qualificationId?: string;
  targetKey?: string;
  /** A flow's proposed WORD, accepted as-is (docs/THRESHOLDS.md §2). */
  word?: string;
  grade?: string;
  note?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const actor = await viewerActor();
    const { statement, qualificationId, ...rest } = input;
    let source: StatementInstanceRef | undefined;
    if (qualificationId === undefined) {
      const resolved = await citedStatement(input.workItemId, statement, await requireViewer());
      if (!resolved.ok) return resolved;
      source = resolved.value;
    }
    const result = await qualifyFact({ ...rest, source, qualificationId }, actor);
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * The COST AT THE ACT for a candidate end date (docs/HEALTH_SIGNALS_V2.md §9,
 * HS-K3's UI half): how many schedule conflicts would stand if this item's
 * end were `candidateEnd` - `dependents` whose declared ends the candidate
 * lands after (the mover's blast on others), and own `dependencies` whose
 * declared ends land after the candidate. A read exposed as an action so the
 * schedule editor can state the blast AT the move - stated, never blocking;
 * the viewer resolves server-side, the read gates on their sight of the item
 * and counts only the neighbours they can open (counts only, no neighbour
 * content). Null = no sight.
 */
export async function scheduleConflictCostsAction(input: {
  workItemId: string;
  candidateEnd: string;
}): Promise<{ dependents: number; dependencies: number } | null> {
  return withResolvedRequest(async () =>
    scheduleConflictCosts(input.workItemId, input.candidateEnd, await requireViewer()),
  );
}

/**
 * Withdraw ONE qualified-statement declaration by its id (HS-G9: a clear
 * targets a declaration, never a whole tag key - clearing Y's adoption must
 * leave X's surviving cause standing). Write-gated in the spine; logged in
 * the clearer's name (HS-G8).
 */
export async function clearQualificationAction(input: {
  workItemId: string;
  qualificationId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await clearQualification(
      input.workItemId,
      input.qualificationId,
      await viewerActor(),
    );
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * Create a named board (docs/VIEWS.md §3/§4), on any layout: owner = the
 * actor, share-ready solo audience, born curated and empty unless the input
 * asks for `scope`, its default `layout` (the kanban unless given), and an
 * optional ROOT item (the set becomes its subtree, a membership setting and
 * never access, and the actor must hold member reach on it). "New checklist" and the item page's
 * two mints are this act. One transaction, one `view_defined` event. Returns
 * the new id so the client can navigate to /boards/<id>.
 */
export async function createBoardAction(
  input: Omit<CreateBoardInput, 'orgId'>,
): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    // Same org resolution as item creation: the commons default org; standing
    // is gated by the spine (requireOrgStanding).
    const orgId = await commonsOrgId();
    if (!orgId) return errOf('not_found', m('common.error.noOrg'));
    const res = await createBoard({ ...input, orgId }, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    await recordViewOpenAtMint(await requireViewer(), res.value.id);
    revalidatePath('/', 'layout');
    return { ok: true, id: res.value.id };
  });
}

/**
 * Create a LENS (docs/LENSES.md §2/§7): a name is the whole ask - the config
 * (criteria, arrangement, rail) is authored on the created thing
 * (create-then-configure). Same org resolution and standing gate as a board's
 * create (`requireOrgStanding` in the spine); born ungranted, so its creator
 * alone reaches it until it is shared by reference (§5). Returns the new id so
 * the client can navigate to /views/<id>.
 */
export async function createLensAction(input: { name: string }): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    const orgId = await commonsOrgId();
    if (!orgId) return errOf('not_found', m('common.error.noOrg'));
    const res = await createLens({ orgId, name: input.name }, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    await recordViewOpenAtMint(await requireViewer(), res.value.id);
    revalidatePath('/', 'layout');
    return { ok: true, id: res.value.id };
  });
}

/** A drag on a board drawn as a checklist (docs/CHECKLIST.md §7): the rank
 *  patch of its top-level lines, written through the spine, gated on the
 *  board there (a rooted board refuses: its lines follow the root). */
export async function setChecklistOrderAction(input: {
  viewId: string;
  order: Record<string, string | null>;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await setChecklistOrder(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * Move a child to directly after a sibling (null = first) in its parent's
 * order (each parent stores its children's order, 2026-09-12): the drag on a
 * checklist SECTION, on a rooted list's top lines (the root's children), or on
 * the item page's sub-items. The server computes the rank against all of the
 * parent's children and gates on the parent, not on the list.
 */
export async function setChildOrderAction(input: {
  parentItemId: string;
  childId: string;
  afterChildId: string | null;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await setChildOrder(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * ONE ACT ON ONE LINE of an item's list (docs/CHECKLIST.md §2, 2026-11-03):
 * add, edit, tick, untick, nest under another line, remove. The item's write
 * gate decides who may, and the item id passes it before anything about the
 * list is read (.claude/rules/write-spine.md rule 7). Answers the list as it
 * stands after the act, so the surface reconciles the line it drew at the tap.
 */
export async function lineAction(input: {
  itemId: string;
  act: LineAct;
}): Promise<{ ok: true; checklist: Checklist } | { ok: false; error: DomainError }> {
  return withResolvedRequest(async () => {
    const res = await actOnLine(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true, checklist: res.value.checklist } : { ok: false, error: res.error };
  });
}

/**
 * Make lines of an item's list into its sub-items, in place
 * (docs/CHECKLIST.md §2): the named lines, or every open top-level line
 * (`'open'`, "Promote all open lines"). Answers the new items' ids, in order.
 */
export async function promoteLinesAction(input: {
  itemId: string;
  lineIds: string[] | 'open';
}): Promise<{ ok: true; ids: string[] } | { ok: false; error: DomainError }> {
  return withResolvedRequest(async () => {
    const res = await promoteLines(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true, ids: res.value.ids } : { ok: false, error: res.error };
  });
}

/**
 * Make a sub-item that holds nothing but its title a line of `carrierId`'s
 * list, at its place, closing the item (docs/CHECKLIST.md §2). `key` is the
 * gesture's idempotency key, which the item's cancel carries.
 */
export async function demoteChildAction(input: {
  carrierId: string;
  childId: string;
  key: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await demoteChild(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * Star or unstar a view for the current viewer - the nav's Favorites (sticky
 * pins). PRIVATE attention state, so it writes OFF the event log
 * (`src/lib/view-stars.ts`), not through the write spine; the actor is the viewer
 * ref, resolved server-side. Idempotent. Revalidates the layout so the nav regroups.
 */
export async function toggleViewStar(input: {
  viewId: string;
  starred: boolean;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const viewer = await requireViewer();
    if (input.starred) await starView(viewer, input.viewId);
    else await unstarView(viewer, input.viewId);
    revalidatePath('/', 'layout');
    return { ok: true };
  });
}

/**
 * Record that the current viewer just opened an item (fired from the client on
 * mount, so it marks a *real* open, never a route prefetch). PRIVATE attention
 * state: writes OFF the event log (`src/lib/item-views.ts`), not through the write
 * spine; the actor is the viewer ref, resolved server-side. Idempotent (advances
 * the timestamp), and records only an item the viewer can open. The mark only
 * shapes the "Last viewed" sort on the *next* Work-list render, so it needs no
 * refresh.
 *
 * Opening the item also reads the mentions it holds for the viewer
 * (docs/PROSE.md §5.4): `read` for each event that named them there
 * (`notification_state`, kind `mentioned`), so the pinned row leaves Activity.
 * That one does refresh, when it wrote something: a cached Activity page must
 * not keep the row.
 */
export async function markItemViewed(itemId: string): Promise<void> {
  return withResolvedRequest(async () => {
    const viewer = await requireViewer();
    // Only an item the viewer can open is recorded: a view of one they cannot
    // open, and the foreign-key error for an id that names no item, would each
    // tell the two apart. A missing, hidden or malformed id returns alike.
    if (!(await getOpenableItemOrg(itemId, viewer))) return;
    await recordItemView(viewer, itemId);
    const read = await recordNotificationActs(viewer, 'read', {
      kind: 'mentioned',
      subjectType: 'work_item',
      subjectId: itemId,
      eventSeqs: await mentionSeqsOn(itemId, viewer),
    });
    if (read > 0) revalidatePath('/activity');
  });
}

/**
 * Record that the current viewer just opened a board (view). Same private,
 * off-log attention state as {@link markItemViewed} (`src/lib/view-opens.ts`);
 * fired from the client on mount so it marks a real open, not a prefetch. Orders
 * the Work list's "group by view" sections on the *next* render - nothing to
 * revalidate here.
 */
export async function markViewOpened(viewId: string): Promise<void> {
  return withResolvedRequest(async () => {
    await recordViewOpen(await requireViewer(), viewId);
  });
}

/** Lazy-load a subject's push rule for the editors (mode + circle only). */
export async function loadPushRule(
  subjectRef: string,
): Promise<{ mode: PushRuleSetting; circleRefs: string[] }> {
  return withResolvedRequest(async () => {
    await requireViewer(); // signed-in only; the rule itself is authority config, not content
    return getPushRule(subjectRef);
  });
}

/**
 * Rename a work item: the `SetTitle` command (one `field_changed` event). A thin
 * wrapper over `runCommand` so the inline title field commits with one call.
 */
export async function renameItem(input: {
  workItemId: string;
  title: string;
  /** The gesture's key, minted on the device; a server key otherwise. */
  key?: string;
}): Promise<CommandResult> {
  // A title typed with `#word` tokens is the `retitle` gesture: the stripped
  // title and its tags in ONE plan (docs/FIELDS_AND_TAGS.md §6.3). A plain
  // rename stays the SetTitle command (see `resolveRetitle` for why).
  if (extractHashtags(input.title).tags.length === 0) {
    return runCommand({
      workItemId: input.workItemId,
      command: { type: 'SetTitle', text: input.title },
    });
  }
  return withResolvedRequest(async () => {
    const res = await act({
      stimulus: stimulusOf({ kind: 'retitle', text: input.title }, input.workItemId, {
        key: input.key ?? randomUUID(),
        source: 'person',
        surface: 'title',
      }),
      actorRef: await requireViewer(),
    });
    if (!res.ok) return { ok: false, error: res.error };
    if (res.value.kind === 'executed') {
      revalidatePath('/', 'layout');
      return { ok: true, at: res.value.item.updatedAt.getTime() };
    }
    if (res.value.kind === 'lane' && res.value.lane === 'propose') {
      revalidatePath('/', 'layout');
      return { ok: true, asked: askedOf(res.value) };
    }
    return {
      ok: false,
      error:
        res.value.kind === 'lane'
          ? domainError('forbidden', res.value.reason)
          : domainError('invalid_state', m('item.error.renameNeedsQuestion')),
    };
  });
}

/**
 * Accept from the review composer (DESIGN.md "Accept mit Auflagen"): the
 * Accept edge's own plan through the resolver (docs/CLOSED_AXIS.md
 * "Finishing": the verdict accepted and the item done), the note for the
 * record, and the follow-up obligations, each a linked contract created in
 * context, all in one transaction (`reviewWithFollowUps`).
 */
export async function acceptWithConditions(input: {
  workItemId: string;
  note?: string;
  followUps?: AcceptFollowUp[];
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await reviewWithFollowUps(
      {
        itemId: input.workItemId,
        edge: 'accept',
        ...(input.note ? { text: input.note } : {}),
        ...(input.followUps ? { followUps: input.followUps } : {}),
      },
      await viewerActor(),
    );
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/**
 * Deny from the review composer - the symmetric "no, and here's the fix": the
 * Deny edge's own plan through the resolver (the verdict denied and the work
 * back to open), its reason for the record, and the follow-up obligations, one
 * transaction (`reviewWithFollowUps`).
 */
export async function denyWithConditions(input: {
  workItemId: string;
  reason?: string;
  followUps?: AcceptFollowUp[];
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await reviewWithFollowUps(
      {
        itemId: input.workItemId,
        edge: 'deny',
        ...(input.reason ? { text: input.reason } : {}),
        ...(input.followUps ? { followUps: input.followUps } : {}),
      },
      await viewerActor(),
    );
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

// --- Flow membership actions (docs/FLOW.md) --------------------------------------
// The item flow strip's verbs. Each is the one flow write spine behind a
// server-resolved actor; a `forbidden` result carries the gate's own legible
// message ("The X gate is enforced; ...") for the UI to surface verbatim.

/** Move a flow member to another stage - the advance command through the gate
 *  machinery (enforced gates refuse, open gates flag, backward moves are free). */
export async function advanceFlowMemberAction(input: {
  flowId: string;
  itemId: string;
  toFlowPhaseKey: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await advanceFlowMember(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/** Move a member across a SELECT flow's options (docs/FLOW_DEFINITION.md S6):
 *  the one dispatch behind lane moves. Who/When re-check server-side (a named
 *  Who / unmet When refuses with its plain-words reason - surface it verbatim
 *  as the disabled/bounce text); a stored target writes the logged position, a
 *  derived target dispatches the Then-acts that make the predicate true. */
export async function moveFlowMemberAction(input: {
  flowId: string;
  itemId: string;
  toOptionKey: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await moveFlowMember(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/** The stage-word menu's one fetch (ruling §7.8): every stage of the member's
 *  flow with its word, the current marker, and the viewer's evaluated move
 *  ability (`stageMoveOptions`), loaded on open. Same non-leaking posture:
 *  an invisible member / unknown flow reads an empty list. */
export async function stageMoveOptionsAction(input: { flowId: string; itemId: string }): Promise<
  | {
      ok: true;
      options: StageMoveOptionView[];
    }
  | { ok: false; error: DomainError }
> {
  return withResolvedRequest(async () => ({
    ok: true as const,
    options: await stageMoveOptions(input.flowId, input.itemId, await requireViewer()),
  }));
}

/** Run the flow-automation engine until drained (docs/FLOW_DEFINITION.md §8b).
 *  The manual wiring point while nothing schedules the engine (no timers -
 *  docs/archive/TECH_DEBT_OPEN_2026-10-23.md "Flow-definition build S6"): any
 *  authenticated viewer may pump it; every machine act the pass dispatches is
 *  gated on the flow's OWN authority, never the caller's. */
export async function runFlowAutomationAction(): Promise<
  { ok: true; processed: number } | { ok: false; error: DomainError }
> {
  return withResolvedRequest(async () => {
    await requireViewer(); // authenticated callers only; the engine acts as the flows
    const { processed } = await runFlowAutomation();
    if (processed > 0) revalidatePath('/', 'layout');
    return { ok: true as const, processed };
  });
}

/** Waive one gate condition for one member (the logged escape; the server gates
 *  it to the gate's gatekeeper or owner reach). */
export async function waiveFlowCondition(input: {
  flowId: string;
  itemId: string;
  flowPhaseKey: string;
  ref: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await waiveGateCondition(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/** Pre-stage work onto a member's gate ("needs to happen in this stage, done
 *  before its gate clears"): one transaction creates the child contract and
 *  appends it to that stage's condition overlay. */
export async function addWorkToFlowGate(input: {
  flowId: string;
  itemId: string;
  flowPhaseKey: string;
  title: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await addWorkToGate(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/** Join an item to a flow at a declared position (default = the first stage).
 *  Joining declares position only - no gates run, no phase is compiled; the
 *  server gates write reach on the item and validates the stage. */
export async function joinFlowAction(input: {
  flowId: string;
  itemId: string;
  flowPhaseKey?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await joinFlow(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/** Remove an item from a flow (the abandoned position rides the event as history). */
export async function leaveFlowAction(input: {
  flowId: string;
  itemId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await leaveFlow(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

/** Hold a flow member - a recorded no-go decision at the current boundary
 *  (docs/DECISIONS.md; the server gates it to the boundary's gatekeeper or
 *  owner reach, and a plain member gets a legible `forbidden`). */
export async function holdFlowMemberAction(input: {
  flowId: string;
  itemId: string;
  reason?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const result = await holdFlowMember(input, await viewerActor());
    if (result.ok) revalidatePath('/', 'layout');
    return result.ok ? { ok: true } : { ok: false, error: result.error };
  });
}

// --- Elevation (sudo) actions (docs/DECISIONS.md) --------------------------------
// A personal, time-boxed pre-consent to overrides the viewer may already make.
// No new rights; org resolved server-side (single-tenant: the default org).

/** The viewer's active elevation, or null. Lazy-loaded by the profile menu
 *  (same pattern as loadPushRule) so the client component need not receive it. */
export async function loadMyElevation(): Promise<ActiveElevation | null> {
  return withResolvedRequest(async () => getMyElevation(await requireViewer()));
}

/** Start (or, if already active, re-affirm) an elevation with a length and an
 *  optional reason. Idempotent server-side. */
export async function elevateAction(input: {
  lengthKey: string;
  reason?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const orgId = await commonsOrgId();
    if (!orgId) return errOf('not_found', m('common.error.noOrgFound'));
    const res = await elevate(
      { orgId, lengthKey: input.lengthKey, reason: input.reason },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** End the viewer's active elevation early. */
export async function deescalateAction(): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const orgId = await commonsOrgId();
    if (!orgId) return errOf('not_found', m('common.error.noOrgFound'));
    const res = await deescalate({ orgId }, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

// --- Ceremony actions (docs/DECISIONS.md "Primitive 3: Ceremony") ----------------
// A convened bundle of decisions. Convening/closing need org standing; adding /
// executing / declining route to the ceremony spine (which gates the real act).

export async function conveneCeremonyAction(input: {
  title: string;
  roster?: CeremonyParticipant[];
}): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    const orgId = await commonsOrgId();
    if (!orgId) return errOf('not_found', m('common.error.noOrgFound'));
    const res = await conveneCeremony(
      { orgId, title: input.title, roster: input.roster },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true, id: res.value.id } : { ok: false, error: res.error };
  });
}

/** The open ceremonies (id + title) the flow strip offers to queue a decision
 *  into. Viewer-gated in the read (docs/COMMONS.md S3: ceremony titles are
 *  content); lazy-loaded by the client. */
export async function loadOpenCeremonies(): Promise<{ id: string; title: string }[]> {
  return withResolvedRequest(async () =>
    (await listCeremonies(await requireViewer()))
      .filter((c) => c.status === 'open')
      .map((c) => ({ id: c.id, title: c.title })),
  );
}

/** The decisions that could be staged for an item (verdict outcomes + per-flow
 *  advance/hold). Lazy-loaded by the ceremony "add decision" form on subject pick. */
export async function loadItemDecisionOptions(itemId: string): Promise<ItemDecisionOptions | null> {
  return withResolvedRequest(async () => itemDecisionOptions(itemId, await requireViewer()));
}

/** Stage a prepared decision onto a ceremony's agenda. */
export async function addCeremonyItemAction(input: {
  ceremonyId: string;
  subjectItemId: string;
  outcome: DecisionOutcome;
  title?: string;
  deciders?: string[];
  flowId?: string;
  toFlowPhaseKey?: string;
  reason?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await addCeremonyItem(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Set (change / remove with a null role) one participant's meeting role. */
export async function setCeremonyParticipantAction(input: {
  ceremonyId: string;
  ref: string;
  role: MeetingRole | null;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await setCeremonyParticipant(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Execute one agenda item (dispatches to the real act, which records the decision). */
export async function executeCeremonyItemAction(input: {
  agendaItemId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await executeCeremonyItem(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Decline one agenda item (the bundle chose not to take it). */
export async function declineCeremonyItemAction(input: {
  agendaItemId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await declineCeremonyItem(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Close a ceremony (its applied decisions stand; pending ones are never taken). */
export async function closeCeremonyAction(input: { ceremonyId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await closeCeremony(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * A typed title as a birth: its `#word` tokens become tags and leave the title
 * (docs/FIELDS_AND_TAGS.md §6.3, "entry is typing"). Refuses an empty title,
 * including one that was only tags. The one parse every create action uses.
 */
function titleAndTags(
  raw: string,
): { ok: true; title: string; tags: string[] } | { ok: false; error: DomainError } {
  const typed = raw.trim();
  if (typed.length === 0) {
    return errOf('invalid_input', m('create.error.titleNeeded'));
  }
  const { text, tags } = extractHashtags(typed);
  if (text.length === 0) {
    return {
      ok: false,
      error: domainError('invalid_input', m('create.error.titleBesidesTags')),
    };
  }
  return { ok: true, title: text, tags };
}

/**
 * Create a contract item (docs/CONTRACT_SPINE.md D9). The creator becomes the requester;
 * the contributor starts empty ("needs doing"). Placement is optional:
 *   - `boardId`: create on a board (birth recipe: grant to scope group, pin if
 *     curated, the Active lane's declaration applied via the mapped act).
 *   - `groupId`: grant to a group directly, no board.
 *   - neither: floating (creator seats only; appears in "Ungrouped" on home).
 * `parentItemId` is independent of placement. The org is resolved server-side.
 */
export async function createItem(input: {
  title: string;
  boardId?: string | null;
  groupId?: string | null;
  parentItemId?: string | null;
  /** The view a line is typed on without being placed on it (a rooted
   *  checklist): lets a member of its audience name the view's root as the
   *  parent, root sight or not (docs/VIEWS.md §2b). */
  viaViewId?: string | null;
  /** Declare the newborn active (the active axis's Active lane composer):
   *  births declared-active via SetActive; every other birthing lane's value
   *  IS the default birth state. */
  bornActive?: boolean | null;
  /** Stage lane at birth (docs/FLOW.md; stage-laned boards only): the write
   *  spine validates it against the board's linked flow. */
  flowPhaseKey?: string | null;
  /** The create's idempotency key, minted on the device at the gesture
   *  (`mintCreateKey`); a retry after a lost reply resends the same one, and
   *  the spine answers it with the item the first arrival made
   *  (docs/LOADING.md §3.4). */
  key: string;
}): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    // Entry is typing (docs/FIELDS_AND_TAGS.md §6.3): `Milch #einkaufen` is
    // "Milch", born tagged #einkaufen.
    const typed = titleAndTags(input.title);
    if (!typed.ok) return typed;
    const { title, tags } = typed;
    // The create target comes from the PLACEMENT (docs/JURISDICTION.md §3.1):
    // the group or the parent the item is born under carries the org; only a
    // floating create lands in the commons. A board placement is org-checked
    // in the write spine itself (createWorkItemTx validates board.orgId ===
    // orgId), and so is the parent - a child never crosses its parent's org.
    // (Until 2026-09-14 a parent-only birth took the commons, so a line added
    // under a jurisdiction root landed in another org than its root.)
    let orgId: string | null = null;
    if (input.groupId) {
      const group = await getGroup(input.groupId, await requireViewer());
      orgId = group?.orgId ?? null;
    } else if (input.parentItemId) {
      orgId = await getItemOrg(input.parentItemId);
    }
    orgId ??= await commonsOrgId();
    if (!orgId) {
      return errOf('not_found', m('common.error.noOrgFound'));
    }
    // The Active lane's birth declaration (a literal true only): every other
    // birthing lane's value is already the newborn's default state.
    const bornActive = input.bornActive === true;
    const actor = await viewerActor();
    const result = await createWorkItem(
      {
        orgId,
        title,
        boardId: input.boardId ?? null,
        groupId: input.groupId ?? null,
        parentItemId: input.parentItemId ?? null,
        viaViewId: input.viaViewId ?? null,
        flowPhaseKey: input.flowPhaseKey ?? null,
        ...(tags.length > 0 ? { tags } : {}),
      },
      actor,
      { commandKey: input.key },
    );
    if (!result.ok) return { ok: false, error: result.error };
    // A newborn is idle/open; the Active lane's composer reaches its lane
    // through the mapped act (docs/CLOSED_AXIS.md - the same write the lane drop
    // dispatches). Flow-staged lanes seed their position via `flowPhaseKey` above.
    if (bornActive) {
      const moved = await execute(result.value.id, { type: 'SetActive', active: true }, actor);
      if (!moved.ok && moved.error.code !== 'no_change') {
        return { ok: false, error: moved.error };
      }
    }
    revalidatePath('/', 'layout');
    return { ok: true, id: result.value.id };
  });
}

/**
 * Create a **group** (a group; the same primitive - MODEL.md §3). The creator is
 * seated as the group's first `owner` by the write spine (`createGroup`), so the
 * people-network has a root of authority to add members from. This is the
 * compose-a-group gesture: a group is a WHO that holds no work, and its board is
 * a lens over what is granted to it - so a fresh group starts empty, not with a
 * seeded backlog. Nesting (parent group) and a named lead are deferred; this
 * creates a top-level group in the viewer's org.
 */
export async function createGroupAction(input: { name: string }): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    const name = input.name.trim();
    if (name.length === 0) {
      return errOf('invalid_input', m('create.error.groupNameNeeded'));
    }
    const actor = await viewerActor();
    // Pre-prod runs one default org (CLAUDE.md tenancy); create in it. The write
    // spine still gates: a viewer with no standing in that org is refused below.
    const orgId = await commonsOrgId();
    if (!orgId) {
      return errOf('not_found', m('common.error.noOrg'));
    }
    try {
      const group = await createGroup({ orgId, name }, actor);
      revalidatePath('/', 'layout');
      return { ok: true, id: group.id };
    } catch (e) {
      // createGroup throws a DomainAbort for expected refusals (no org standing);
      // surface its typed error, let infrastructure failures propagate.
      if (e instanceof DomainAbort) return { ok: false, error: e.domainError };
      throw e;
    }
  });
}

/**
 * Start working in one move: Claim (seat yourself) and declare it active
 * (`SetActive(true)` - the MACHINE reshape; activity is a declared flag, not a
 * phase move). Contract spine (docs/CONTRACT_SPINE.md §6): these are two
 * **explicit** intents chained through ordinary commands, not an inferred side
 * effect - Start never auto-claims by itself; this composite is the user asking
 * for both. The log reads claim -> activity declared, exactly what happened.
 */
export async function startWorking(workItemId: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const actor = await viewerActor();
    const claimed = await execute(workItemId, { type: 'Claim' }, actor);
    if (!claimed.ok && claimed.error.code !== 'no_change') {
      return { ok: false, error: claimed.error };
    }
    const started = await execute(workItemId, { type: 'SetActive', active: true }, actor);
    if (!started.ok && started.error.code !== 'no_change') {
      return { ok: false, error: started.error };
    }
    revalidatePath('/', 'layout');
    return { ok: true };
  });
}

/**
 * **A tick, through the resolver** (docs/RESOLUTION.md §2 and §12 step 3): the
 * ONE server action behind every tick and un-tick on a checklist. It sends the
 * STIMULUS, never a plan and never a command list - the meaning is the
 * server's, computed from the truth at act time in one transaction (§1 step 7).
 *
 * It answers with three things the surface needs and nothing else: the plan's
 * one sentence, what the item still WAITS on (the ticked line's quiet trailing
 * note), and the new truth of every item the plan TOUCHED, so the client
 * patches them instead of refreshing the whole lens (§10: "the real cost today
 * is the refresh"). The full refresh rides the SSE ping, as it does today.
 *
 * **A plan spans a set** (2026-09-22): a tick sweeps its open sub-tasks
 * (§6) and an un-tick reverses the whole plan through the origin
 * marker (§8), so the rows come back for every item the steps name - one
 * gather, batched, the same shape the single row already had.
 *
 * A gesture that the viewer may not take comes back as its typed refusal; the
 * lock on the line is the same predicate, so the lane is the rare race, not
 * the ordinary path.
 */
export async function tickAction(input: {
  workItemId: string;
  kind: 'tick' | 'untick';
  /** The gesture's idempotency key, minted on the device (docs/MOBILE.md §8). */
  key: string;
  /** The surface the gesture came from - an ARGUMENT, never a code path (§5). */
  surface: string;
  /** The device time of the tap, ISO-8601. */
  deviceTime?: string;
}): Promise<TickResult> {
  return withResolvedRequest(async () => {
    const viewerRef = await requireViewer();
    const res = await act({
      stimulus: {
        kind: input.kind,
        itemId: input.workItemId,
        key: input.key,
        source: 'person',
        surface: input.surface,
        ...(input.deviceTime ? { deviceTime: input.deviceTime } : {}),
      },
      actorRef: viewerRef,
    });
    if (!res.ok) return { ok: false, error: res.error };
    const outcome = res.value;
    if (outcome.kind === 'lane' && outcome.lane === 'propose') {
      revalidatePath('/', 'layout');
      return { ok: true, asked: askedOf(outcome) };
    }
    if (outcome.kind !== 'executed') {
      return {
        ok: false,
        error:
          outcome.kind === 'lane'
            ? domainError('forbidden', outcome.reason)
            : outcome.kind === 'question'
              ? domainError('invalid_state', outcome.question.text)
              : domainError('invalid_state', m('lists.error.needsConfirmation')),
      };
    }
    // The cached render is now stale for a NAVIGATION; the surface that acted
    // patches its own rows from `rows` below rather than refreshing the lens.
    revalidatePath('/', 'layout');
    // The tapped item first, then everything else the plan's steps named, each
    // once and in the order the plan reached it.
    const touched = [...new Set([input.workItemId, ...outcome.plan.steps.map((s) => s.itemId)])];
    const rows = await readListRows(viewerRef, touched);
    return {
      ok: true,
      statement: outcome.plan.statement,
      ...(outcome.plan.waits ? { waits: outcome.plan.waits } : {}),
      rows: touched.flatMap((id) => {
        const row = rows.get(id);
        return row ? [row] : [];
      }),
      at: outcome.item.updatedAt.getTime(),
    };
  });
}

/** The lane's answer in the shape every surface speaks. */
function askedOf(outcome: {
  statement: Message;
  answerers: readonly string[];
  changeId: string;
}): AskedAnswer {
  return {
    statement: outcome.statement,
    answerers: [...outcome.answerers],
    changeId: outcome.changeId,
  };
}

/**
 * **The lock as the entry** (docs/CHECKLIST.md §7, docs/RESOLUTION.md §12 step
 * 8): a line the viewer may only VIEW is tappable, and the tap ASKS. The tick's
 * stimulus is filed with the item's owners as a proposal carrying the act -
 * accepting it lands the tick as the owner's own, attributed to the asker.
 *
 * It goes straight to the proposal rather than through `act`: the write gate
 * refuses a viewer before the resolver ever runs (`gateForWriteFacts`), and the
 * ask needs only SIGHT. One ask per person per line: a second tap returns the
 * pending one (`dedupeKey`) instead of stacking.
 *
 * The other half of the lock - asking for ACCESS so the tapper can tick
 * themselves (the request-access lane, MODEL.md §6) - is direction, not
 * settled (the board item "The lock as an act-request").
 */
export async function proposeTickAction(input: {
  workItemId: string;
  /** The gesture's idempotency key, minted on the device. */
  key: string;
}): Promise<{ ok: true; asked: AskedAnswer } | { ok: false; error: DomainError }> {
  return withResolvedRequest(async () => {
    const viewerRef = await requireViewer();
    const res = await proposeAct(
      {
        itemId: input.workItemId,
        stimulusKey: input.key,
        stimulus: { kind: 'tick' },
        dedupeKey: `ask:${viewerRef}:tick`,
      },
      directActor(parseRef(viewerRef)),
    );
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, asked: askedOf(res.value) };
  });
}

/**
 * **THE ITEM PAGE'S ONE DISPATCH** (docs/RESOLUTION.md §12 step 5): every act
 * and every value the page offers - the act sheet's verbs, the state line's
 * chips, the "+"'s values, the kebab's way out, the stage chip's move - sends
 * its STIMULUS here, and the resolver decides what it means against the truth
 * at act time, in one transaction (§1 step 7).
 *
 * It replaces the compositions RESOLUTION §0 lists: `completePersonal` (the
 * personal collapse, retired by §2's "no tick accepts" - personal work's
 * conclusion is the tick's own plan, Tender then Close) and the command loop of
 * `runCommands`, which ran N transactions and stopped at the first refusal.
 *
 * The client sends a GESTURE, never a plan and never a command list; the
 * gesture's own kinds are the only ones admitted here (an `event` stimulus is
 * the log's, never a browser's). `previewPlanId` is the determinism check: when
 * the truth moved between the page's dry run and the act, the meaning moved
 * with it and the act aborts rather than executing one nobody saw.
 *
 * A QUESTION comes back as a question (§6's conclusion question, the out-of-role
 * confirmation), because the page can ask it; a lane and a refusal come back as
 * their typed error, which the row that sent them already renders.
 */
export type ItemActResult =
  | { ok: true; outcome: 'executed'; statement: Message; at: number }
  | { ok: true; outcome: 'question'; question: Question; consequences: Message[] }
  | { ok: true; outcome: 'asked'; asked: AskedAnswer }
  | { ok: false; error: DomainError };

export async function actOnItem(input: {
  workItemId: string;
  /** What the row means, minus its head - the server stamps source and surface. */
  gesture: PendingStimulus;
  /** The gesture's idempotency key, minted on the device (docs/MOBILE.md §8). */
  key: string;
  /** The surface it came from - an ARGUMENT, never a code path (§5). */
  surface?: string;
  /** The device time of the tap, ISO-8601. */
  deviceTime?: string;
  /** Answers already given to this gesture's questions (§4). */
  answers?: Answer[];
  /** The id of the plan the page previewed (§1 step 7). */
  previewPlanId?: string;
}): Promise<ItemActResult> {
  return withResolvedRequest(async () => {
    // The gesture is the client's: rebuilt by the domain's decoder (the same
    // rule the offline replay applies) before the resolver sees it. `event`,
    // the log's own stimulus (§8), is not a kind it knows.
    const gesture = decodePendingStimulus(input.gesture);
    if (!gesture.ok) return { ok: false, error: gesture.error };
    const viewerRef = await requireViewer();
    const res = await act({
      stimulus: stimulusOf(gesture.value, input.workItemId, {
        key: input.key,
        source: 'person',
        surface: input.surface ?? 'item-page',
        ...(input.deviceTime ? { deviceTime: input.deviceTime } : {}),
      }),
      actorRef: viewerRef,
      ...(input.answers ? { answers: input.answers } : {}),
      ...(input.previewPlanId ? { previewPlanId: input.previewPlanId } : {}),
    });
    if (!res.ok) return { ok: false, error: res.error };
    const outcome = res.value;
    if (outcome.kind === 'question') {
      return { ok: true, outcome: 'question', question: outcome.question, consequences: [] };
    }
    if (outcome.kind === 'confirm') {
      return {
        ok: true,
        outcome: 'question',
        // A plan carrying a confirmation is returned, never executed (§6).
        question: outcome.plan.confirm!,
        consequences: outcome.plan.consequences,
      };
    }
    if (outcome.kind === 'lane') {
      // The propose lane LANDED (§12 step 8): the gesture is now an ask with
      // the owners, and the page says so. The request-access lane still only
      // reports - the row that sent it already reads locked.
      if (outcome.lane === 'propose') {
        revalidatePath('/', 'layout');
        return { ok: true, outcome: 'asked', asked: askedOf(outcome) };
      }
      return { ok: false, error: domainError('forbidden', outcome.reason) };
    }
    revalidatePath('/', 'layout');
    return {
      ok: true,
      outcome: 'executed',
      statement: outcome.plan.statement,
      at: outcome.item.updatedAt.getTime(),
    };
  });
}

/**
 * The tag picker's candidates (docs/FIELDS_AND_TAGS.md §6.4): the tags on work
 * the viewer can see, ranked by the vote law - with `itemId`, near that item
 * first and without the tags it already wears. Names and counts only; the
 * ranking stays on the server.
 */
export async function tagOptionsAction(itemId?: string): Promise<TagOption[]> {
  return withResolvedRequest(async () => {
    const viewer = await requireViewer();
    const ranked = await tagCandidates(viewer, { ...(itemId ? { itemId } : {}), limit: 200 });
    return ranked.map(({ tag, fold, count, teams }) => ({ tag, fold, count, teams }));
  });
}

/**
 * The retag's reach, before the act (docs/FIELDS_AND_TAGS.md §6.2): how many
 * visible carriers of the tag (or its subtree) the viewer may edit, and how
 * many keep the old tag. Counts only.
 */
export async function retagReachAction(fromFold: string): Promise<{ act: number; view: number }> {
  return withResolvedRequest(async () => {
    const reach = await retagReach(await requireViewer(), foldTag(fromFold));
    return { act: reach.act.length, view: reach.view };
  });
}

/** The retag itself: one plan over the viewer's reach (`src/server/retag.ts`). */
export async function retagAction(input: {
  from: string;
  to: string;
  key: string;
}): Promise<
  | { ok: true; statement: Message; changed: number; skipped: number; undoItemId: string }
  | { ok: false; error: DomainError }
> {
  return withResolvedRequest(async () => {
    const res = await retag(input, await requireViewer());
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return {
      ok: true,
      statement: res.value.statement,
      changed: res.value.changed,
      skipped: res.value.skipped,
      undoItemId: res.value.firstItemId,
    };
  });
}

/** Grant a role on an item to **any** grantee - a person or a group - or revoke it. The
 *  generalised form behind the item's Access panel (`shareItem` was group-only); the role
 *  cascades to the subtree (MODEL §3). Granting `owner` to a non-requester is a maintainer. */
export async function grantItemRole(input: {
  workItemId: string;
  granteeRef: string; // "person:<id>" | "group:<id>"
  role: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(() =>
    runItemCommand(input.workItemId, (orgId, actor) =>
      grantRole(
        { orgId, workItemId: input.workItemId, granteeRef: input.granteeRef, role: input.role },
        actor,
      ),
    ),
  );
}

export async function revokeItemRole(input: {
  workItemId: string;
  granteeRef: string;
}): Promise<CommandResult> {
  return withResolvedRequest(() =>
    runItemCommand(input.workItemId, (orgId, actor) =>
      revokeRole({ orgId, workItemId: input.workItemId, granteeRef: input.granteeRef }, actor),
    ),
  );
}

/** Request a role on a protected item you cannot yet reach (the discoverability
 *  request-access lane, MODEL §6). Records a pending grant for an owner to approve. */
export async function requestItemAccess(input: {
  workItemId: string;
  role?: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(() =>
    runItemCommand(input.workItemId, (orgId, actor) =>
      requestAccess({ orgId, workItemId: input.workItemId, role: input.role ?? 'viewer' }, actor),
    ),
  );
}

/**
 * Ask for access to the items this one inherits a grant from that the viewer
 * cannot see (docs/MODEL.md "Discoverability and request-access", Leon
 * 2026-10-05): the access panel shows such a source as unknown and sends no
 * id of it, so the ask names the item the panel is on and the server finds the
 * sources (`requestInheritedAccess`). It answers alike whatever it found or
 * recorded; an item the viewer cannot open answers as a missing one.
 */
export async function requestInheritedItemAccess(input: {
  workItemId: string;
  role?: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(async () =>
    runInOrg(await openableOrgOf(input.workItemId), async (orgId, actor) => {
      const res = await requestInheritedAccess(
        { orgId, workItemId: input.workItemId, role: input.role ?? 'viewer' },
        actor,
      );
      return res.ok ? { ok: true } : { ok: false, error: res.error };
    }),
  );
}

// --- Work-item invites (docs/archive/COMMONS_BUILD_PLAN.md S4) ------------------------
// Adding a collaborator by email: an existing person goes straight to a grant
// (grantItemRole above); anyone else gets an invite - a pending, email-bound
// grant that converts on their verified signup. The action resolves the actor
// server-side, gates via the spine (full sight, like granting), and delivers the
// link through the one mail seam.

/** Invite an email onto an item as `role`; on success the emailed link carries the
 *  claim token to `/invite/<token>`. The row is committed by the spine before the
 *  send; a send failure surfaces typed (the pending invite still shows, and a
 *  re-invite refreshes its token). */
export async function inviteToItem(input: {
  workItemId: string;
  email: string;
  role: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const orgId = await getItemOrg(input.workItemId);
    if (!orgId)
      return { ok: false, error: domainError('not_found', m('common.error.itemNotFound')) };
    const res = await createInvite(
      { orgId, workItemId: input.workItemId, email: input.email, role: input.role },
      await viewerActor(),
    );
    if (!res.ok) return res;
    // A registered address was granted directly (no token, no email) - nothing to send.
    if (res.value.kind === 'granted') {
      revalidatePath('/', 'layout');
      return { ok: true };
    }
    const url = `${env.BETTER_AUTH_URL}/invite/${res.value.rawToken}`;
    try {
      // Written in the recipient's language where the address has an account
      // with a setting, else the inviter's (`mailLocale`).
      const to = res.value.invite.email;
      await sendMail({ to, ...renderMail(await mailLocale(to), itemInviteMail(url)) });
    } catch (err) {
      console.error('[invites] send failed:', err);
      return {
        ok: false,
        error: domainError('internal', m('item.error.inviteMailFailed')),
      };
    }
    revalidatePath('/', 'layout');
    return { ok: true };
  });
}

/** Withdraw a pending invite (inviter or item owner). */
export async function revokeItemInvite(input: { inviteId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await revokeInvite({ inviteId: input.inviteId }, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Claim an invite by its emailed token, as the signed-in viewer. Returns the item
 *  id to land on (the landing page redirects there). One neutral error for any dead
 *  token; a mismatched/unverified email gets the honest typed refusal. */
export async function claimInviteAction(input: {
  token: string;
}): Promise<{ ok: true; workItemId: string } | { ok: false; error: DomainError }> {
  return withResolvedRequest(async () => {
    const res = await claimInvite(input.token, await viewerActor());
    if (!res.ok) return res;
    revalidatePath('/', 'layout');
    return { ok: true, workItemId: res.value.workItemId };
  });
}

// --- Contacts (docs/REACHABILITY.md §2; src/server/contacts.ts) ---------------
// The mutual link that makes two people who share no work nameable to each
// other. Identity-plane state, off the log. The actor is always the viewer:
// nothing here takes a person ref from the client except the OTHER half of a
// pair the viewer is in, which the spine re-checks.

/** What the account page's Contacts section shows: the viewer's contacts, the
 *  requests awaiting them, what they sent, whom they blocked, and the asks to
 *  join a group addressed to them (docs/REACHABILITY.md §2.4), with those
 *  withdrawn in the last 30 days (Block is their only act). */
export type ContactsSectionData = ContactLists & {
  asks: MembershipAskView[];
  withdrawnAsks: WithdrawnAskView[];
};

export async function loadContacts(): Promise<ContactsSectionData> {
  return withResolvedRequest(async () => {
    const viewer = await requireViewer();
    const [lists, asks, withdrawnAsks] = await Promise.all([
      listContactsFor(viewer),
      listMembershipAsksFor(viewer),
      listWithdrawnAsksFor(viewer),
    ]);
    return { ...lists, asks, withdrawnAsks };
  });
}

/**
 * Ask someone to be a contact by the exact handle or address they gave you.
 * Answers the same whatever the identifier found (no oracle, REACHABILITY.md):
 * every request leaves a row in the viewer's own sent list, and a miss leaves
 * nothing anywhere else.
 */
export async function requestContact(identifier: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await serverRequestContact(identifier, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Withdraw a request from the viewer's sent list. Always succeeds. */
export async function withdrawContact(sentId: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await withdrawContactRequest(sentId, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Accept a request addressed to the viewer. */
export async function acceptContactRequest(personRef: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await acceptContact(personRef, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Decline a request addressed to the viewer, or drop an established contact
 *  (either side). */
export async function dropContact(personRef: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await removeContact(personRef, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Block a person from a request addressed to the viewer or from a contact
 *  (removing it); their future requests behave as a miss. */
export async function blockPerson(personRef: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await blockContact(personRef, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Lift a block the viewer set. */
export async function unblockPerson(personRef: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await unblockContact(personRef, await viewerActor());
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Accept an ask to join a group addressed to the viewer (the stranger
 *  landing). The membership names the group; the viewer has no sight of it
 *  yet, so nothing here resolves the group first. */
export async function acceptGroupInvitation(membershipId: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await acceptMembership({ membershipId }, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Decline an ask to join a group addressed to the viewer. */
export async function declineGroupInvitation(membershipId: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await declineMembership({ membershipId }, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

// --- Handles (docs/REACHABILITY.md §1; src/server/handles.ts) ------------------
// The viewer's own chosen identifier. Identity-plane state, off the log.

/** Pick or change the viewer's handle. */
export async function saveHandle(input: { handle: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await setHandle(input.handle, await viewerActor());
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Give up the viewer's handle (it stays parked for everyone else). */
export async function removeHandle(): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await clearHandle(await viewerActor());
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

// --- Agent keys (/account; src/server/machine-keys.ts) ------------------------
// A person mints a key that lets an agent act as `automation:<machine>` with
// their authority. The raw key crosses the wire exactly once, in the mint
// result; the store keeps the hash. Owner-only by construction: the actor is
// the viewer, and the read filters on the viewer.

/** Mint an agent key for the viewer. The returned rawKey is shown once.
 *  `scope` states the key's level, where it acts and optionally its items
 *  (src/domain/machine-scope.ts); there is no default, and full authority is
 *  the explicit `FULL_MACHINE_SCOPE`. */
export async function mintAgentKey(input: {
  machineId: string;
  label: string;
  scope: MachineScope;
}): Promise<
  | { ok: true; rawKey: string; key: { id: string; machineId: string; label: string } }
  | { ok: false; error: DomainError }
> {
  return withResolvedRequest(async () => {
    const res = await mintMachineKey(
      { machineId: input.machineId, label: input.label, scope: input.scope },
      await viewerActor(),
    );
    if (!res.ok) return res;
    revalidatePath('/', 'layout');
    const { key, rawKey } = res.value;
    return { ok: true, rawKey, key: { id: key.id, machineId: key.machineId, label: key.label } };
  });
}

/** Revoke one of the viewer's agent keys. Someone else's key answers not_found. */
export async function revokeAgentKey(input: { keyId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await revokeMachineKey({ keyId: input.keyId }, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

// --- Group administration (the membership panel) -----------------------------
// All owner-gated in the server layer; the org is resolved from the group so the
// caller only needs the group id.

async function groupOrg(
  groupId: string,
): Promise<{ ok: true; orgId: string } | { ok: false; error: DomainError }> {
  // Viewer-gated (docs/COMMONS.md S3): an invisible group answers not_found,
  // exactly like a nonexistent id - no oracle through the admin actions.
  const group = await getGroup(groupId, await requireViewer());
  if (!group) return errOf('not_found', m('common.error.groupNotFound'));
  return { ok: true, orgId: group.orgId };
}

/**
 * Describe the group - what it is for, in its own words (the group page's
 * second section). Owner-gated in the spine; a non-owner never sees the editor,
 * and a forged call is refused there.
 */
export async function saveGroupDescription(input: {
  groupId: string;
  description: string | null;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const org = await groupOrg(input.groupId);
    if (!org.ok) return org;
    const res = await setGroupDescription(
      { groupId: input.groupId, description: input.description },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

export async function addGroupMember(input: {
  groupId: string;
  memberRef: string;
  role: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const org = await groupOrg(input.groupId);
    if (!org.ok) return org;
    const res = await addMember(
      { orgId: org.orgId, groupId: input.groupId, memberRef: input.memberRef, role: input.role },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

export async function removeGroupMember(input: {
  groupId: string;
  memberRef: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const org = await groupOrg(input.groupId);
    if (!org.ok) return org;
    const res = await removeMember(
      { orgId: org.orgId, groupId: input.groupId, memberRef: input.memberRef },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

export async function setGroupMemberRole(input: {
  groupId: string;
  memberRef: string;
  role: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const org = await groupOrg(input.groupId);
    if (!org.ok) return org;
    const res = await setMemberRole(
      { orgId: org.orgId, groupId: input.groupId, memberRef: input.memberRef, role: input.role },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Propose nesting `childGroupId` under `parentGroupId` (the panel's group is one
 *  side; the org is taken from `parentGroupId`). Owner-gated, two-sided. */
export async function proposeGroupNesting(input: {
  parentGroupId: string;
  childGroupId: string;
  role: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const org = await groupOrg(input.parentGroupId);
    if (!org.ok) return org;
    const res = await proposeNesting(
      {
        orgId: org.orgId,
        parentGroupId: input.parentGroupId,
        childGroupId: input.childGroupId,
        role: input.role,
      },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Accept a pending nesting proposal. `groupId` (the panel's group) carries the org. */
export async function acceptGroupNesting(input: {
  groupId: string;
  membershipId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const org = await groupOrg(input.groupId);
    if (!org.ok) return org;
    const res = await acceptNesting(
      { orgId: org.orgId, membershipId: input.membershipId },
      await viewerActor(),
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Set (or clear, with `value: null`) a module field's value on an item. The
 *  key may be a registry module or a context-sourced `ctx:<ownerRef>` definition
 *  (docs/CONTEXT_RESOLUTION.md). */
export async function setModuleField(input: {
  workItemId: string;
  moduleKey: string;
  fieldKey: string;
  value: unknown;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await serverSetModuleField(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Pin (add) or unpin (remove) a global registry module on an item
 *  (docs/VIEWS.md §2a: modules are progressive disclosure, adding is a visual
 *  pin). Shared item state, evented on the item. */
export async function setItemModule(input: {
  workItemId: string;
  moduleKey: ModuleKey;
  present: boolean;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await serverSetItemModulePresence(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Configure a board (owner-gated), on every layout: its default layout, the
 *  list layout's columns and column sort, the kanban's lane source, manual
 *  lanes, requires and flow link, its membership source/criteria and its ROOT
 *  item. Its name is `renameViewAction`'s. */
export async function configureBoardAction(input: ConfigureBoardInput): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await configureBoard(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Assign an item to a manual lane on a board (view config, item untouched). */
/**
 * Hide or unhide an item on a board, on any layout. This is **shared board config**,
 * not a change to the item: the write spine appends to the board (member-gated) and the
 * item is untouched. Hiding just stops the board tracking it. It still lives on the
 * graph and any other board it is on. The item page's Views picker hides an item from a
 * board rooted on an item above it (docs/VIEWS.md §2b "An item's views").
 */
export async function setBoardHidden(input: {
  viewId: string;
  itemId: string;
  hidden: boolean;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await serverSetBoardItemHidden(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * Route a stage-lane drop (docs/FLOW.md "Flows tie views together"). Which verb
 * a drop means depends on membership, resolved SERVER-side (never trusted from
 * the client): a MEMBER moved to a stage lane goes through the ONE stage-move
 * endpoint (`moveFlowMember`, docs/FLOW_DEFINITION.md S6/§10 - Who/When
 * re-checked with plain-words refusals, entry applies the stage's values
 * atomically, deny on any failure); an unstaged card dropped into a lane JOINS
 * at that stage (a declaration, no gates); a member dropped into the unstaged
 * lane LEAVES. Each branch is the one flow write spine; the typed DomainError
 * comes back intact so the board can toast the refusal's own words - a refused
 * drop is stated, never a silent bounce (ruling §7.5).
 */
async function stageMove(
  move: { workItemId: string; flowId: string; toFlowPhaseKey: string | null },
  actor: ActorContext,
): Promise<{ ok: true } | { ok: false; error: DomainError }> {
  const memberships =
    (await flowsOfItems([move.workItemId], await requireViewer())).get(move.workItemId) ?? [];
  const isMember = memberships.some((m) => m.flowId === move.flowId);
  const res =
    move.toFlowPhaseKey === null
      ? isMember
        ? await leaveFlow({ flowId: move.flowId, itemId: move.workItemId }, actor)
        : { ok: true as const } // already unstaged: nothing to write
      : isMember
        ? await moveFlowMember(
            { flowId: move.flowId, itemId: move.workItemId, toOptionKey: move.toFlowPhaseKey },
            actor,
          )
        : await joinFlow(
            { flowId: move.flowId, itemId: move.workItemId, flowPhaseKey: move.toFlowPhaseKey },
            actor,
          );
  return res.ok ? { ok: true } : { ok: false, error: res.error };
}

/**
 * Commit a board drop in ONE round trip: the optional lane move AND the manual
 * order, then a single revalidate. A cross-lane drop used to fire two server
 * actions (the lane write + `setBoardOrder`), each revalidating the whole layout;
 * this folds them so a drag is one POST and one revalidation.
 *
 * The lane write depends on the board's source. An attribute lane and a
 * member's stage move are STIMULI (docs/RESOLUTION.md §12 step 5): the drop
 * sends what it means - `{attribute: value}`, or the stage to move to - and the
 * resolver decides the whole meaning in one transaction, the same way the item
 * page's chip menus and act sheet do. `flowPhase` still routes the two moves
 * that are not moves WITHIN a flow (an unstaged card dropped into a lane JOINS
 * at that stage, a member dropped into unstaged LEAVES); `manual` writes the
 * view's lane assignment. (Lane-by-a-field was retired with context fields, docs/VIEWS.md
 * §2a.) Reordering (the rank patch) is
 * shared board config on the board's own row, and the two are independent writes that
 * don't clobber each other. Either part may be absent: an in-lane reorder
 * passes no `move`, a pure lane change passes an empty `order`.
 */
export async function commitBoardMove(input: {
  viewId: string;
  order: Record<string, string | null>;
  move?:
    | { kind: 'stimulus'; workItemId: string; key: string; gesture: PendingStimulus }
    | { kind: 'flowPhase'; workItemId: string; flowId: string; toFlowPhaseKey: string | null }
    | {
        kind: 'flowField';
        workItemId: string;
        flowId: string;
        fieldKey: string;
        value: string | null;
      }
    | { kind: 'manual'; itemId: string; laneId: string | null };
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const actor = await viewerActor();
    const { viewId, order, move } = input;
    if (move) {
      let mv: { ok: true; asked?: AskedAnswer } | { ok: false; error: DomainError };
      if (move.kind === 'stimulus') {
        // ONE dispatch (docs/RESOLUTION.md §12 step 5): the drop's meaning is
        // the resolver's, in one transaction. A QUESTION comes back as the
        // refusal the board speaks on the drop - the lane has no surface to
        // answer it on, and §6's question lives where it can be answered.
        const gesture = decodePendingStimulus(move.gesture);
        if (!gesture.ok) return { ok: false, error: gesture.error };
        const res = await act({
          stimulus: stimulusOf(gesture.value, move.workItemId, {
            key: move.key,
            source: 'person',
            surface: 'lane',
          }),
          actorRef: formatRef(actor.principal),
        });
        mv = !res.ok
          ? { ok: false, error: res.error }
          : res.value.kind === 'executed'
            ? { ok: true }
            : res.value.kind === 'lane' && res.value.lane === 'propose'
              ? // The drop became an ask (§12 step 8): nothing moved, the board
                // says who was asked and puts the card back.
                { ok: true, asked: askedOf(res.value) }
              : {
                  ok: false,
                  error:
                    res.value.kind === 'lane'
                      ? domainError('forbidden', res.value.reason)
                      : res.value.kind === 'question'
                        ? domainError('invalid_state', res.value.question.text)
                        : res.value.plan.confirm
                          ? domainError('invalid_state', res.value.plan.confirm.text)
                          : domainError('invalid_state', m('boards.error.needsConfirmation')),
                };
      } else if (move.kind === 'flowPhase') {
        mv = await stageMove(move, actor);
      } else if (move.kind === 'flowField') {
        // A field-lane drop writes the flow field on the ITEM (logged item
        // truth through the one module write spine; the flow's own validation
        // applies - a non-member card refuses there).
        mv = await serverSetModuleField(
          {
            workItemId: move.workItemId,
            moduleKey: `flow:${move.flowId}`,
            fieldKey: move.fieldKey,
            value: move.value,
          },
          actor,
        );
      } else {
        mv = await setItemLane({ viewId, itemId: move.itemId, laneId: move.laneId }, actor);
      }
      if (!mv.ok) return { ok: false, error: mv.error };
      // An ask moved nothing, so the drop's order has nothing to follow.
      if (mv.asked) {
        revalidatePath('/', 'layout');
        return { ok: true, asked: mv.asked };
      }
    }
    if (Object.keys(order).length > 0) {
      const ord = await serverSetBoardItemOrder({ viewId, order }, actor);
      if (!ord.ok) return { ok: false, error: ord.error };
    }
    revalidatePath('/', 'layout');
    return { ok: true };
  });
}

// --- Proposals (docs/PROPOSALS.md): author a staged changeset, resolve a raised diff -----
// Authoring/publishing mutate the proposal resource; applying a change routes through the
// item write spine inside the server fn, so the truth still changes the one sanctioned way.
// All wired to the current viewer (server-resolved), revalidating the app like every action.

/** Open a draft proposal in an org the viewer has standing in (returns its id). */
export async function createProposalAction(input: {
  orgId: string;
  name: string;
}): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    const res = await createProposal(input, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, id: res.value.id };
  });
}

/** Stage (or re-stage) a "set field F on item I to V" op into a draft proposal. */
export async function stageProposalChange(input: {
  proposalId: string;
  targetItemId: string;
  fieldPath: string;
  value: unknown;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await addChange(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Drop a staged change from a draft proposal. */
export async function removeProposalChange(input: { changeId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await removeChange(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Freeze a draft and roll it out: its changes become the raised diffs owners see. */
export async function publishProposalAction(proposalId: string): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await publishProposal(proposalId, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** What accepting a raised change answers with: done, or - for a change that
 *  carries an ACT - the question its run raised (§6's conclusion, the
 *  out-of-role confirmation), for the accepter to answer and send again. */
export type ApplyChangeResult =
  | { ok: true; outcome: 'applied' }
  | { ok: true; outcome: 'question'; question: Question; consequences: Message[] }
  | { ok: false; error: DomainError };

/** Apply a raised change to its target item (routes through the item write spine;
 *  an act runs through the resolver as the accepter, docs/RESOLUTION.md §12 step 8). */
export async function applyProposalChange(input: {
  changeId: string;
  /** Answers to the question a previous attempt returned (§4). */
  answers?: Answer[];
}): Promise<ApplyChangeResult> {
  return withResolvedRequest(async () => {
    const res = await applyChange(input, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    if (res.value.kind === 'question') {
      return {
        ok: true,
        outcome: 'question',
        question: res.value.question,
        consequences: res.value.consequences,
      };
    }
    revalidatePath('/', 'layout');
    return { ok: true, outcome: 'applied' };
  });
}

/** Decline a raised change (the owner side refuses the directive); the diff closes. */
export async function declineProposalChange(input: {
  changeId: string;
  reason?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await declineChange(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * Propose a single change on an item in one move: open a proposal, stage the change, and
 * publish it (so it is raised to the item's owner immediately). The org is resolved from the
 * item. This is the compact "propose a change" composer on the item detail; the multi-change
 * draft tray is a later iteration. A non-owner proposing into work they don't own is the
 * overreach case, recorded and flagged by the spine on apply, never blocked here.
 */
export async function proposeChange(input: {
  targetItemId: string;
  fieldPath: string;
  value: unknown;
  name?: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const actor = await viewerActor();
    const orgId = await getItemOrg(input.targetItemId);
    if (!orgId)
      return { ok: false, error: domainError('not_found', m('common.error.itemNotFound')) };
    // Atomic (F2): create + stage + publish in ONE transaction inside the spine,
    // so a sight-gate refusal on the target rolls back the proposal too - a probe
    // of a hidden id writes nothing and gets the oracle-safe not_found.
    const res = await proposeItemChange(
      {
        orgId,
        targetItemId: input.targetItemId,
        fieldPath: input.fieldPath,
        value: input.value,
        name: input.name,
      },
      actor,
    );
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true };
  });
}

// --- Plans (an owned collection of proposals; docs/PROPOSALS.md) --------------

export type ExecuteResult =
  | { ok: true; applied: number; raised: number }
  | { ok: false; error: DomainError };
export type StageResult = { ok: true; planId: string } | { ok: false; error: DomainError };

/** Open a draft plan scoped to a group the viewer belongs to (boards model). Returns its id. */
export async function createPlanAction(input: {
  scopeRef: string;
  name: string;
}): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    const res = await createPlan(input, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, id: res.value.id };
  });
}

/**
 * Execute a plan (the plan owner): it comes into motion - in-role changes (the author owns the
 * target) apply directly, the rest are raised to their owners. Returns the applied/raised tally.
 */
export async function executePlanAction(planId: string): Promise<ExecuteResult> {
  return withResolvedRequest(async () => {
    const res = await executePlan(planId, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, applied: res.value.applied, raised: res.value.raised };
  });
}

/**
 * Enter planning from an item: stage a change into a plan, new or existing, during its planning
 * phase (nothing is executed). With `newPlanName` a plan is created first; otherwise it lands in
 * `planId`. The change goes into the viewer's own draft proposal in that plan. Returns the plan id
 * so the UI can jump to the plan.
 */
export async function stagePlanChange(input: {
  targetItemId: string;
  fieldPath: string;
  value: unknown;
  planId?: string;
  newPlanName?: string;
  /** group ref to scope a new plan to (the item's home group, from the composer) */
  newPlanScopeRef?: string;
}): Promise<StageResult> {
  return withResolvedRequest(async () => {
    const actor = await viewerActor();
    let planId = input.planId;
    if (!planId) {
      if (!input.newPlanScopeRef) {
        return {
          ok: false,
          error: domainError('invalid_input', m('plans.error.groupNeeded')),
        };
      }
      const created = await createPlan(
        { scopeRef: input.newPlanScopeRef, name: input.newPlanName?.trim() || 'New plan' },
        actor,
      );
      if (!created.ok) return { ok: false, error: created.error };
      planId = created.value.id;
    }
    const res = await stageChangeInPlan(
      {
        planId,
        targetItemId: input.targetItemId,
        fieldPath: input.fieldPath,
        value: input.value,
      },
      actor,
    );
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, planId };
  });
}

// --- Board pins (docs/CONTRACT_SPINE.md §9, D6) ---------------------------------
// A pin is a curator's explicit placement of an item on a board, independent of
// the group grant. Idempotent: re-pinning / re-unpinning is a no-op.

/** Pin an item to a board (member-gated on the view; share-authority when it
 *  has an audience). On a flow-linked stage-laned board the pin also joins the
 *  item to the linked flow (docs/VIEWS.md §6 as amended 2026-07-16); the
 *  result carries the typed join outcome so the UI can quietly flag a refused
 *  join on a landed pin (skip+flag, never silence). */
export async function pinItemAction(input: {
  viewId: string;
  itemId: string;
}): Promise<{ ok: true; flowJoin: PinFlowJoin } | { ok: false; error: DomainError }> {
  return withResolvedRequest(async () => {
    const res = await pinItem(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true, flowJoin: res.value.flowJoin } : { ok: false, error: res.error };
  });
}

/** Unpin an item from a board or a list (member-gated on the view). */
export async function unpinItemAction(input: {
  viewId: string;
  itemId: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await unpinItem(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * Dev-only quick persona switch: a **real** sign-in as a seeded demo persona, replacing
 * the old `wg_actor` impersonation cookie. One click in the profile menu establishes a
 * genuine better-auth session as that persona, so multi-user testing stays fast WITHOUT
 * an unauthenticated identity path (the demo accounts exist and are pre-verified; sign-in
 * runs the normal email/password flow).
 *
 * Hard-gated to non-production: it throws in production (and the profile-menu section that
 * calls it is not rendered there), so there is no impersonation surface to ship.
 */
export async function devSignInAs(personaId: string): Promise<void> {
  // Never in production, and the demo cast only (`devSignInTarget`, unit-tested).
  const target = devSignInTarget(env.NODE_ENV, personaId);
  if (!target.ok) throw new Error(target.reason);
  const { id } = target;
  await auth.api.signInEmail({
    body: { email: demoEmail(id), password: DEMO_PASSWORD },
    headers: await headers(),
  });
  revalidatePath('/', 'layout');
}

// --- View audience + sharing (docs/VIEWS.md §2b/§5, slice 4) ------------------
// A view's audience is its live access (view_grants); "sharing" a view is
// minting a NEW one seeded from a source (§5), never an in-place mutation of
// the source. The actor is always resolved server-side.

/** Lazy-load one view's audience + the viewer's governance standing for the
 *  audience editor (gated in the read: unreachable = null, like nonexistent). */
export async function loadViewAccess(viewId: string): Promise<ViewAccessDetail | null> {
  return withResolvedRequest(async () => getViewAccess(viewId, await requireViewer()));
}

/** Grant a person/group a role on a view - grow its audience (owner-gated in
 *  the spine; upserts, so a role change rides the same action). */
export async function grantViewAccessAction(input: {
  viewId: string;
  granteeRef: string;
  role?: Role;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await grantViewAccess(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Revoke a person/group's audience grant on a view (owner-gated in the spine).
 *  Curated members stop inheriting it live; nothing on the items to unwind. */
export async function revokeViewAccessAction(input: {
  viewId: string;
  granteeRef: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await revokeViewAccess(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

// --- Managing a board or a view (docs/VIEWS.md §2b "Managing a board") ------
// Rename, Archive, Restore and Leave, for a board and for a view
// alike. Each gate lives in the spine (src/server/views.ts); the actor is
// always the viewer, resolved server-side.

/** Rename a board or a view (the owner or a co-owner). */
export async function renameViewAction(input: {
  viewId: string;
  name: string;
}): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await renameView(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Archive a board or a view for everyone (an owner of it). */
export async function archiveViewAction(input: { viewId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await archiveView(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Restore an archived board or view (an owner of it). */
export async function restoreViewAction(input: { viewId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const res = await restoreView(input, await viewerActor());
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/** Leave a board or a view: the viewer's own grant goes, nothing else moves.
 *  The grantee is the viewer, never a ref the client names. The last owner
 *  of an active one stays (`lastOwnerStays`). */
export async function leaveViewAction(input: { viewId: string }): Promise<CommandResult> {
  return withResolvedRequest(async () => {
    const actor = await viewerActor();
    const res = await revokeViewAccess(
      { viewId: input.viewId, granteeRef: formatRef(actor.principal) },
      actor,
    );
    if (res.ok) revalidatePath('/', 'layout');
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  });
}

/**
 * "Share" = mint a NEW shared view seeded from a source (docs/VIEWS.md §5,
 * D-share: best-effort copy). The actor must be able to READ the source; the
 * new view is theirs, with the chosen initial audience; the source is
 * untouched. Returns the new id so the client can navigate to it.
 */
export async function mintSharedViewAction(input: MintSharedViewInput): Promise<CreateResult> {
  return withResolvedRequest(async () => {
    const res = await mintSharedView(input, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    await recordViewOpenAtMint(await requireViewer(), res.value.id);
    revalidatePath('/', 'layout');
    return { ok: true, id: res.value.id };
  });
}

/**
 * Lazy count for the Share-the-work dialog (docs/VIEWS.md §7): how many items
 * the VIEWER's own assembly of this view holds - exactly the set the bulk
 * grant below would process. Gated in the read (`boardMemberItemIds`): an
 * unreachable view answers null, like a nonexistent id.
 */
export async function loadShareableItemCount(viewId: string): Promise<number | null> {
  return withResolvedRequest(async () => {
    const ids = await boardMemberItemIds(viewId, await requireViewer());
    return ids === null ? null : ids.length;
  });
}

export type BulkGrantItemsActionResult =
  | ({ ok: true } & BulkGrantItemsResult)
  | { ok: false; error: DomainError };

/**
 * "Share the work" - the §7 bulk item grant (docs/VIEWS.md §7, slice 6): grant
 * every item on a view the actor is authorised to share to the named grantees.
 * All authority is server-side (`bulkGrantItems` - the view cloak, the member
 * act-through gate, per-item share-authority); the result carries the exact
 * granted / already-shared / skipped split so the UI shows the flags rather
 * than swallowing them.
 */
export async function bulkGrantItemsAction(input: {
  viewId: string;
  granteeRefs: string[];
  role?: Role;
}): Promise<BulkGrantItemsActionResult> {
  return withResolvedRequest(async () => {
    const res = await bulkGrantItems(input, await viewerActor());
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, ...res.value };
  });
}

export type RemoveAttachmentResult = { ok: true; id: string } | { ok: false; error: DomainError };

/**
 * Remove a file from its item (docs/ATTACHMENTS.md §6). The id comes from the
 * client and passes one sight check in the spine before anything is read for
 * it: an id that names nothing, a removed file and a file on an item the
 * viewer cannot open answer alike (`itemErrors.attachment.notFound`).
 */
export async function removeAttachment(attachmentId: string): Promise<RemoveAttachmentResult> {
  return withResolvedRequest(async () => {
    const res = await removeAttachmentOnSpine(await viewerActor(), attachmentId);
    if (!res.ok) return { ok: false, error: res.error };
    revalidatePath('/', 'layout');
    return { ok: true, id: res.value.id };
  });
}
